import { useConfirmDeleteModal } from '@/features/invoice_reports/components/ConfirmDeleteModal';
import { useConfirmCancelModal } from '@/features/invoice_reports/components/ConfirmCancelModal';
import { useConfirmDuplicateModal } from '@/features/invoice_reports/components/ConfirmDuplicateModal';
import type { ReportActionState } from '@/features/invoice_reports/types';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ButtonGroup,
  ButtonV2,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback } from 'react';
import styles from './HeaderButtons.module.scss';

const cx = classnames.bind(styles);

type HeaderButtonsProps = {
  reportId: string;
  detailPath?: string;
  editPath?: string;
  editAction?: ReportActionState;
  deleteAction?: ReportActionState;
  cancelAction?: ReportActionState;
  isCopyable?: boolean;
  reportNumber?: string;
  reportTitle?: string;
  reportTotalAmount?: string | null;
  apPayeeName?: string | null;
};

export const HeaderButtons: FC<HeaderButtonsProps> = memo(
  ({
    reportId,
    detailPath,
    editPath,
    editAction,
    deleteAction,
    cancelAction,
    isCopyable,
    reportNumber,
    reportTitle,
    reportTotalAmount,
    apPayeeName,
  }) => {
    const { t } = useTranslation();
    const { onOpenConfirmDuplicateModal } = useConfirmDuplicateModal();
    const { onOpenConfirmDeleteModal } = useConfirmDeleteModal();
    const { onOpenConfirmCancelModal } = useConfirmCancelModal();
    const container = useGlobalContainerRef();

    const handleDetailClick = useCallback(() => {
      window.location.href = detailPath ?? `/invoice_reports/${reportId}`;
    }, [detailPath, reportId]);

    const handleEditClick = useCallback(() => {
      if (editPath) window.location.href = editPath;
    }, [editPath]);

    const handleDuplicateClick = useCallback(() => {
      onOpenConfirmDuplicateModal({
        id: reportId,
        number: reportNumber,
        title: reportTitle,
        apPayeeName: apPayeeName,
        totalAmount: reportTotalAmount,
      });
    }, [
      reportId,
      onOpenConfirmDuplicateModal,
      reportNumber,
      reportTitle,
      apPayeeName,
      reportTotalAmount,
    ]);

    const handleDelete = useCallback(() => {
      if (!reportId) return;
      onOpenConfirmDeleteModal({
        id: reportId,
        number: reportNumber,
        title: reportTitle,
        apPayeeName,
        totalAmount: reportTotalAmount,
      });
    }, [
      reportId,
      reportNumber,
      reportTitle,
      apPayeeName,
      reportTotalAmount,
      onOpenConfirmDeleteModal,
    ]);

    const handleCancel = useCallback(() => {
      onOpenConfirmCancelModal({
        id: reportId,
        number: reportNumber,
        title: reportTitle,
        apPayeeName,
        totalAmount: reportTotalAmount,
      });
    }, [
      reportId,
      reportNumber,
      reportTitle,
      apPayeeName,
      reportTotalAmount,
      onOpenConfirmCancelModal,
    ]);

    const isEditDisabled = editAction !== undefined && !editAction.allowed;
    const isDeleteDisabled =
      deleteAction !== undefined && !deleteAction.allowed;
    const isCancelDisabled =
      cancelAction !== undefined && !cancelAction.allowed;

    return (
      <ButtonGroup>
        {cancelAction?.visible &&
          (isCancelDisabled && cancelAction.reason ? (
            <Tooltip
              title={cancelAction.reason}
              overlayClassName={cx('editTooltip')}
              getPopupContainer={container}
            >
              <span className={cx('dangerButton')}>
                <ButtonV2 size='sm' isTransparent color='danger' isDisabled>
                  {t('ap_report_status_canceled')}
                </ButtonV2>
              </span>
            </Tooltip>
          ) : (
            <span className={cx('dangerButton')}>
              <ButtonV2
                size='sm'
                isTransparent
                color='danger'
                isDisabled={isCancelDisabled}
                onClick={handleCancel}
              >
                {t('ap_report_status_canceled')}
              </ButtonV2>
            </span>
          ))}
        {deleteAction?.visible &&
          (isDeleteDisabled && deleteAction.reason ? (
            <Tooltip
              title={deleteAction.reason}
              overlayClassName={cx('editTooltip')}
              getPopupContainer={container}
            >
              <span className={cx('dangerButton')}>
                <ButtonV2 size='sm' isTransparent color='danger' isDisabled>
                  {t('delete')}
                </ButtonV2>
              </span>
            </Tooltip>
          ) : (
            <span className={cx('dangerButton')}>
              <ButtonV2
                size='sm'
                isTransparent
                color='danger'
                isDisabled={isDeleteDisabled}
                onClick={handleDelete}
              >
                {t('delete')}
              </ButtonV2>
            </span>
          ))}
        <ButtonV2
          size='sm'
          isTertiary
          isDisabled={!isCopyable}
          onClick={handleDuplicateClick}
        >
          {t('duplicate')}
        </ButtonV2>
        <ButtonV2 size='sm' isTertiary onClick={handleDetailClick}>
          {t('detail')}
        </ButtonV2>
        {editAction?.visible &&
          (isEditDisabled && editAction.reason ? (
            <Tooltip
              title={editAction.reason}
              overlayClassName={cx('editTooltip')}
              getPopupContainer={container}
            >
              <ButtonV2 size='sm' isSecondary isDisabled>
                {t('edit')}
              </ButtonV2>
            </Tooltip>
          ) : (
            <ButtonV2
              size='sm'
              isSecondary
              isDisabled={isEditDisabled}
              onClick={handleEditClick}
            >
              {t('edit')}
            </ButtonV2>
          ))}
      </ButtonGroup>
    );
  }
);

HeaderButtons.displayName = 'HeaderButtons';
